import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  Sparkles, Loader2, CheckCircle2, 
  ArrowLeft, Users, PoundSterling, TrendingUp, Award, Share2,
  ArrowRight, Layers, UserPlus, Tag, Mail, Lock, User
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import AppNav from '@/components/AppNav';
import Footer from '@/components/Footer';
import axios from 'axios';
import SEO from '@/components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

const AffiliateInterestPage = () => {
  const { isAuthenticated, getAuthHeader, register: authRegister, login: authLogin } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [affiliateCode, setAffiliateCode] = useState('');
  const [parentCode, setParentCode] = useState('');
  const [signupMode, setSignupMode] = useState('register'); // 'register' or 'login'
  const [signupData, setSignupData] = useState({ name: '', email: '', password: '' });

  useEffect(() => {
    if (isAuthenticated) {
      axios.get(`${API}/api/affiliates/me`, { headers: getAuthHeader() })
        .then(res => {
          // Already registered — redirect to dashboard
          navigate('/affiliate-dashboard');
        })
        .catch(() => { /* not an affiliate yet */ });
    }
  }, [isAuthenticated]);

  const goToDashboard = () => {
    navigate('/affiliate-dashboard');
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const res = await axios.post(
        `${API}/api/affiliates/register`,
        { parent_code: parentCode || null },
        { headers: getAuthHeader() }
      );
      toast.success('You are now an affiliate! Redirecting to your dashboard...');
      navigate('/affiliate-dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO title="Earn with Poettik — Affiliate Programme" description="Join the Poettik affiliate programme. Earn up to 40% recurring commission on every monthly subscription you refer." />
      {/* Animated background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/10 rounded-full blur-3xl animate-pulse-glow" />
        <div className="absolute bottom-20 -left-40 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative z-50 border-b border-border/50 bg-background/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <AppNav
              extraLinks={isAuthenticated ? [
                { to: '/affiliate-dashboard', label: 'Affiliate Dashboard', icon: <Users className="w-4 h-4" /> },
              ] : []}
            />
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 text-accent mb-6">
            <Users className="w-4 h-4" />
            <span className="text-sm font-medium">Affiliate Programme</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Earn Up To <span className="gradient-text-accent">40%</span> Commission
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Join our multi-level affiliate programme and earn recurring commissions on every subscription you refer - plus earn from your team's sales too!
          </p>
        </div>

        {/* Commission Structure */}
        <Card variant="gradient" className="mb-12 p-8">
          <h2 className="font-display text-2xl font-bold mb-6 text-center">3-Level Commission Structure</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="text-center p-6 rounded-xl bg-background/50">
              <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                <span className="font-display text-2xl font-bold text-primary">30%</span>
              </div>
              <h3 className="font-semibold text-lg mb-2">Level 1 - Direct Sales</h3>
              <p className="text-sm text-muted-foreground">
                Earn 30% commission on every customer you refer directly
              </p>
            </div>
            
            <div className="text-center p-6 rounded-xl bg-background/50">
              <div className="w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center mx-auto mb-4">
                <span className="font-display text-2xl font-bold text-accent">7%</span>
              </div>
              <h3 className="font-semibold text-lg mb-2">Level 2 - Sub-Affiliate Sales</h3>
              <p className="text-sm text-muted-foreground">
                Earn 7% when affiliates you recruit make sales
              </p>
            </div>
            
            <div className="text-center p-6 rounded-xl bg-background/50">
              <div className="w-16 h-16 rounded-full bg-secondary/20 flex items-center justify-center mx-auto mb-4">
                <span className="font-display text-2xl font-bold text-secondary">3%</span>
              </div>
              <h3 className="font-semibold text-lg mb-2">Level 3 - Network Sales</h3>
              <p className="text-sm text-muted-foreground">
                Earn 3% from your sub-affiliates' recruits
              </p>
            </div>
          </div>

          {/* Example Earnings */}
          <div className="bg-background/30 rounded-xl p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <PoundSterling className="w-5 h-5 text-primary" />
              Example: Pro Subscription (£15.99/month)
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm mb-6">
              <div className="flex justify-between items-center p-3 bg-background/50 rounded-lg">
                <span>Your direct sale:</span>
                <span className="font-bold text-primary">£4.80/month</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-background/50 rounded-lg">
                <span>Sub-affiliate sale:</span>
                <span className="font-bold text-accent">£1.12/month</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-background/50 rounded-lg">
                <span>Level 3 sale:</span>
                <span className="font-bold text-secondary">£0.48/month</span>
              </div>
            </div>

            {/* Scaling example */}
            <div className="border-t border-border/30 pt-4">
              <h4 className="font-semibold mb-3 text-sm">Scaling Example*</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center p-3 bg-background/50 rounded-lg">
                  <span>10 affiliates sign up under your link (Level 1 — 30% | 10 total)</span>
                  <span className="font-bold text-accent">£48.00/month</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-background/50 rounded-lg">
                  <span>5 affiliates sign up under each of theirs (Level 2 — 7% | 50 total)</span>
                  <span className="font-bold text-accent">£56.00/month</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-background/50 rounded-lg">
                  <span>5 more sign up under those (Level 3 — 3% | 250 total)</span>
                  <span className="font-bold text-accent">£120.00/month</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg border border-primary/20">
                  <span className="font-semibold">Total potential monthly earnings*:</span>
                  <span className="font-bold text-xl text-foreground">£224.00/month</span>
                </div>
              </div>

              {/* Tree Diagram - Pyramid (narrow top, wide bottom) */}
              <div className="mt-6 p-5 bg-background/50 rounded-xl overflow-hidden">
                <h4 className="font-semibold text-sm text-center mb-1">Your Affiliate Network</h4>
                <p className="text-xs text-muted-foreground text-center mb-5">3 levels of earning potential</p>
                <div className="flex flex-col items-center">
                  {/* You - Top */}
                  <div className="px-4 py-2 rounded-lg bg-gradient-to-r from-primary to-primary-glow text-primary-foreground font-bold text-sm">
                    YOU
                  </div>
                  {/* Branches to Level 1 */}
                  <div className="relative w-full flex justify-center py-3">
                    <svg className="w-48 h-8" viewBox="0 0 192 32">
                      <line x1="96" y1="0" x2="32" y2="32" stroke="hsl(176 93% 55%)" strokeWidth="1.5" opacity="0.6" />
                      <line x1="96" y1="0" x2="64" y2="32" stroke="hsl(176 93% 55%)" strokeWidth="1.5" opacity="0.6" />
                      <line x1="96" y1="0" x2="96" y2="32" stroke="hsl(176 93% 55%)" strokeWidth="1.5" opacity="0.6" />
                      <line x1="96" y1="0" x2="128" y2="32" stroke="hsl(176 93% 55%)" strokeWidth="1.5" opacity="0.6" />
                      <line x1="96" y1="0" x2="160" y2="32" stroke="hsl(176 93% 55%)" strokeWidth="1.5" opacity="0.6" />
                    </svg>
                  </div>
                  {/* Level 1 - 5 large dots */}
                  <div className="flex items-center gap-3 mb-1">
                    {[...Array(5)].map((_, i) => <div key={i} className="w-7 h-7 rounded-full bg-primary/20 border border-primary/40 flex items-center justify-center"><div className="w-2.5 h-2.5 rounded-full bg-primary" /></div>)}
                  </div>
                  <div className="px-3 py-1 rounded-full bg-primary/15 border border-primary/30 text-xs font-medium mt-1 mb-4">
                    <span className="text-primary font-bold">Level 1</span> <span className="text-primary">(30%)</span>
                  </div>
                  {/* Level 2 - 10 medium dots */}
                  <div className="flex items-center gap-2 justify-center mb-1">
                    {[...Array(10)].map((_, i) => <div key={i} className="w-5 h-5 rounded-full bg-accent/20 border border-accent/40 flex items-center justify-center"><div className="w-1.5 h-1.5 rounded-full bg-accent" /></div>)}
                  </div>
                  <div className="px-3 py-1 rounded-full bg-accent/15 border border-accent/30 text-xs font-medium mt-1 mb-4">
                    <span className="text-accent font-bold">Level 2</span> <span className="text-accent">(7%)</span>
                  </div>
                  {/* Level 3 - 60 small dots, widest */}
                  <div className="flex flex-col items-center gap-1 mb-1 w-full max-w-xl">
                    <div className="flex items-center gap-1 justify-center">
                      {[...Array(20)].map((_, i) => <div key={i} className="w-3.5 h-3.5 rounded-full bg-secondary/20 border border-secondary/40 flex items-center justify-center"><div className="w-1 h-1 rounded-full bg-secondary" /></div>)}
                    </div>
                    <div className="flex items-center gap-1 justify-center">
                      {[...Array(20)].map((_, i) => <div key={`b${i}`} className="w-3.5 h-3.5 rounded-full bg-secondary/20 border border-secondary/40 flex items-center justify-center"><div className="w-1 h-1 rounded-full bg-secondary" /></div>)}
                    </div>
                    <div className="flex items-center gap-1 justify-center">
                      {[...Array(20)].map((_, i) => <div key={`c${i}`} className="w-3.5 h-3.5 rounded-full bg-secondary/20 border border-secondary/40 flex items-center justify-center"><div className="w-1 h-1 rounded-full bg-secondary" /></div>)}
                    </div>
                  </div>
                  <div className="px-3 py-1 rounded-full bg-secondary/15 border border-secondary/30 text-xs font-medium mt-1">
                    <span className="text-secondary font-bold">Level 3</span> <span className="text-secondary">(3%)</span>
                  </div>
                </div>
              </div>

              <p className="text-sm font-medium text-foreground mt-4 text-center">
                Based on 10 Level 1 + 50 Level 2 + 250 Level 3 subscribers on the Pro plan (£15.99/month)
              </p>
              <p className="text-xs text-muted-foreground mt-2 text-center">
                * Commissions are recurring for as long as the customer remains subscribed
              </p>
              <p className="text-xs text-muted-foreground mt-1 text-center">
                * Earnings shown are an illustrative example only and are not guaranteed to provide any income
              </p>
            </div>
          </div>
        </Card>

        {/* Benefits */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          <Card variant="glass" className="p-6 text-center">
            <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center mx-auto mb-4">
              <PoundSterling className="w-6 h-6 text-primary" />
            </div>
            <h3 className="font-semibold mb-2">Recurring Income</h3>
            <p className="text-sm text-muted-foreground">Earn every month your referrals stay subscribed</p>
          </Card>
          
          <Card variant="glass" className="p-6 text-center">
            <div className="w-12 h-12 rounded-xl bg-accent/20 flex items-center justify-center mx-auto mb-4">
              <Layers className="w-6 h-6 text-accent" />
            </div>
            <h3 className="font-semibold mb-2">3-Level Earnings</h3>
            <p className="text-sm text-muted-foreground">Build a team and earn from their success too</p>
          </Card>
          
          <Card variant="glass" className="p-6 text-center">
            <div className="w-12 h-12 rounded-xl bg-secondary/20 flex items-center justify-center mx-auto mb-4">
              <TrendingUp className="w-6 h-6 text-secondary" />
            </div>
            <h3 className="font-semibold mb-2">Growing Market</h3>
            <p className="text-sm text-muted-foreground">TikTok Shop is booming - get in early</p>
          </Card>
          
          <Card variant="glass" className="p-6 text-center">
            <div className="w-12 h-12 rounded-xl bg-success/20 flex items-center justify-center mx-auto mb-4">
              <Award className="w-6 h-6 text-success" />
            </div>
            <h3 className="font-semibold mb-2">Real-Time Tracking</h3>
            <p className="text-sm text-muted-foreground">Dashboard to track all your earnings</p>
          </Card>
        </div>

        {/* How It Works */}
        <Card variant="glass" className="mb-12 p-8">
          <h2 className="font-display text-2xl font-bold mb-6 text-center">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { step: "1", title: "Sign Up", desc: "Create a Poettik account and join the affiliate programme" },
              { step: "2", title: "Get Your Code", desc: "Receive your unique affiliate code and referral link" },
              { step: "3", title: "Share & Promote", desc: "Share Poettik with your audience using your link" },
              { step: "4", title: "Earn Monthly", desc: "Get paid for every subscription - recurring!" },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center mx-auto mb-3">
                  <span className="font-bold text-primary-foreground">{item.step}</span>
                </div>
                <h3 className="font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </Card>

        {/* Registration / CTA Section */}
        {registered ? (
          <Card variant="gradient" className="p-8 text-center max-w-lg mx-auto mb-12" data-testid="affiliate-success-card">
            <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-primary" />
            </div>
            <h2 className="font-display text-2xl font-bold mb-4">You're In!</h2>
            <p className="text-muted-foreground mb-2">Your unique affiliate code is:</p>
            <p className="text-2xl font-mono font-bold text-primary mb-6" data-testid="affiliate-code-display">{affiliateCode}</p>
            <p className="text-sm text-muted-foreground mb-6">
              Share this code with others to start earning commissions on their subscriptions.
            </p>
            <Button variant="premium" size="lg" onClick={() => navigate('/affiliate-dashboard')} data-testid="go-to-dashboard-btn">
              <Share2 className="w-5 h-5 mr-2" />
              Go to Affiliate Dashboard
            </Button>
          </Card>
        ) : isAuthenticated ? (
          <Card variant="glass" className="p-8 max-w-lg mx-auto mb-12" data-testid="affiliate-register-card">
            <CardHeader className="text-center px-0 pt-0">
              <CardTitle className="font-display text-2xl">Join the Affiliate Programme</CardTitle>
              <CardDescription>
                Register now and get your unique affiliate code instantly
              </CardDescription>
            </CardHeader>

            <CardContent className="px-0 pb-0">
              <form onSubmit={handleRegister} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="parent_code">Referral Code (optional)</Label>
                  <div className="relative">
                    <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="parent_code"
                      placeholder="Enter referral code if you were invited"
                      className="pl-10"
                      value={parentCode}
                      onChange={(e) => setParentCode(e.target.value)}
                      data-testid="affiliate-parent-code-input"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    If someone referred you, enter their affiliate code here
                  </p>
                </div>

                <Button
                  type="submit"
                  variant="premium"
                  size="lg"
                  className="w-full"
                  disabled={isLoading}
                  data-testid="affiliate-register-btn"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Registering...
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-5 h-5 mr-2" />
                      Join the Programme
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        ) : (
          <Card variant="glass" className="p-8 max-w-lg mx-auto mb-12" data-testid="affiliate-signup-card">
            <CardHeader className="text-center px-0 pt-0">
              <CardTitle className="font-display text-2xl">
                {signupMode === 'register' ? 'Create Account & Join' : 'Sign In & Join'}
              </CardTitle>
              <CardDescription>
                {signupMode === 'register'
                  ? 'Create your Poettik account to get your affiliate code'
                  : 'Sign in to your existing account'}
              </CardDescription>
            </CardHeader>

            <CardContent className="px-0 pb-0">
              <form onSubmit={async (e) => {
                e.preventDefault();
                setIsLoading(true);
                try {
                  let token;
                  if (signupMode === 'register') {
                    const res = await authRegister(signupData.name, signupData.email, signupData.password);
                    token = res.token;
                  } else {
                    const res = await authLogin(signupData.email, signupData.password);
                    token = res.token;
                  }
                  // Now register as affiliate
                  const affRes = await axios.post(
                    `${API}/api/affiliates/register`,
                    { parent_code: parentCode || null },
                    { headers: { Authorization: `Bearer ${token}` } }
                  );
                  toast.success('You are now an affiliate! Redirecting to your dashboard...');
                  navigate('/affiliate-dashboard');
                } catch (error) {
                  const msg = error.response?.data?.detail || 'Something went wrong. Please try again.';
                  toast.error(msg);
                } finally {
                  setIsLoading(false);
                }
              }} className="space-y-4">
                {signupMode === 'register' && (
                  <div className="space-y-2">
                    <Label>Full Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Your name"
                        className="pl-10"
                        value={signupData.name}
                        onChange={e => setSignupData(d => ({ ...d, name: e.target.value }))}
                        required
                        data-testid="affiliate-signup-name"
                      />
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="email"
                      placeholder="you@example.com"
                      className="pl-10"
                      value={signupData.email}
                      onChange={e => setSignupData(d => ({ ...d, email: e.target.value }))}
                      required
                      data-testid="affiliate-signup-email"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="password"
                      placeholder={signupMode === 'register' ? 'Min 6 characters' : 'Your password'}
                      className="pl-10"
                      value={signupData.password}
                      onChange={e => setSignupData(d => ({ ...d, password: e.target.value }))}
                      required
                      data-testid="affiliate-signup-password"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Referral Code (optional)</Label>
                  <div className="relative">
                    <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Enter code if someone referred you"
                      className="pl-10"
                      value={parentCode}
                      onChange={e => setParentCode(e.target.value)}
                      data-testid="affiliate-signup-parent-code"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  variant="premium"
                  size="lg"
                  className="w-full"
                  disabled={isLoading}
                  data-testid="affiliate-signup-submit"
                >
                  {isLoading ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Processing...</>
                  ) : (
                    <><UserPlus className="w-5 h-5 mr-2" /> {signupMode === 'register' ? 'Create Account & Join' : 'Sign In & Join'}</>
                  )}
                </Button>
                <p className="text-center text-sm text-muted-foreground">
                  {signupMode === 'register' ? 'Already have an account?' : 'New to Poettik?'}{' '}
                  <button type="button" className="text-primary hover:underline" onClick={() => setSignupMode(m => m === 'register' ? 'login' : 'register')}>
                    {signupMode === 'register' ? 'Sign in instead' : 'Create an account'}
                  </button>
                </p>
              </form>
            </CardContent>
          </Card>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default AffiliateInterestPage;
